import { MediaMatcher } from '@angular/cdk/layout';
import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
  effect,
  computed,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DateAdapter, MAT_DATE_FORMATS, MatDateFormats } from '@angular/material/core';
import { Router, RouterOutlet } from '@angular/router';
import {
  ApplicationSwitcherApp,
  HeaderComponent,
  ApplicationSwitcherComponent,
} from '@claas/claas-ui-components/navigation/header';
import { NavigationItem } from '@claas/claas-ui-components/navigation/navigation';
import { Store } from '@ngrx/store';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ConvertPipe, ConvertPipeService } from 'cde-fe-convert-pipe';
import { IOrganisationListItem, IUser } from 'cde-fe-organization-registration-dialog';
import { getLocaleByAlpha2 } from 'country-locale-map';
import moment from 'moment';
import { BehaviorSubject, Subscription } from 'rxjs';
import SurveyWrapperComponent from '@cde-fe-app/app/features/survey/survey-wrapper.component';
import { getDashboardIsInitialized } from '@cde-fe-app/app/shared/state/dashboard';
import { dealerStore } from '@cde-fe-app/app/shared/state/dealer/dealer.store';
import { featureToggleStore } from '@cde-fe-app/app/shared/state/feature-toggle/feature-toggle.store';
import {
  getRegistrationDefaultOrganisation,
  getCurrentOrganisation,
  getLastSelectedOrganisation,
  getOrganisationChanging,
  getRegisterOrganisationPending,
  getRehydratePending,
  getUserPermissions,
} from '@cde-fe-app/app/shared/state/organisations';
import { partsShopDealerStore } from '@cde-fe-app/app/shared/state/parts-shop/parts-shop-dealer/parts-shop-dealer.store';
import { replaceOrUpdateQueryParam } from '@cde-fe-app/app/shared/utils/append-or-replace-orgId-param';
import { screens } from '@cde-fe-app/app/shared/utils/screen-query-wrapper';
import { environment } from '@cde-fe-app/environments/environment';
import { HelpMenuComponent } from './features/navbar/views/right-navbar/help-menu/help-menu.component';
import { RightNavbarComponent } from './features/navbar/views/right-navbar/right-navbar.component';
import { CdeSidenavComponent } from './shared/components/cde-sidenav/cde-sidenav/cde-sidenav.component';
import { Features } from './shared/models/feature-toggle.model';
import { ICurrentOrganisation, RegistrationStatus } from './shared/models/organisation.model';
import { AuthServiceWrapper } from './shared/services/auth.service';
import { DashboardService } from './shared/services/dashboard.service';
import { EventTrackingService } from './shared/services/event-tracking.service';
import { MachinesService } from './shared/services/machines.service';
import { MaintenanceTextService } from './shared/services/maintenance-text.service';
import { RoutingStateService } from './shared/services/routing-state.service';
import { IState } from './shared/state';
import { getPendingOrgSwitch, userSelector } from './shared/state/auth';
import { CheckLogin } from './shared/state/auth/auth.actions';
import { MachineImagesUrl } from './shared/state/machine-overview/machines.actions';
import { GetCurrentOrganisation } from './shared/state/organisations/organisations.actions';
import {
  FeatureToggleDescription,
  getFeatures,
  IFeatureToggleNavigationItem,
  navigationItems,
  permissionsPerRoute,
} from './shared/utils/nav-items';

// - 'no' (Norwegian)   -> moment only ships 'nb'
// - 'br' (Brazilian PT) -> moment's own 'br' locale is Breton, not Portuguese
// - 'ebu' (Embu, Kenya) -> moment has no locale for it at all
const LANGUAGE_TO_MOMENT_LOCALE: Record<string, string> = {
  no: 'nb',
  br: 'pt-br',
  ebu: 'en',
};

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [ConvertPipe],
  imports: [
    HeaderComponent,
    ApplicationSwitcherComponent,
    RightNavbarComponent,
    CdeSidenavComponent,
    RouterOutlet,
    AsyncPipe,
    TranslateModule,
    SurveyWrapperComponent,
    HelpMenuComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  readonly auth = inject(AuthServiceWrapper);
  readonly cdr = inject(ChangeDetectorRef);
  readonly convertPipeService = inject(ConvertPipeService);
  readonly dashboardService = inject(DashboardService);
  readonly eventTrackingService = inject(EventTrackingService);
  readonly machineService = inject(MachinesService);
  readonly router = inject(Router);
  readonly store = inject<Store<IState>>(Store<IState>);
  readonly translator = inject(TranslateService);
  readonly routingState = inject(RoutingStateService);
  readonly maintenanceTextService = inject(MaintenanceTextService);
  readonly dealerStore = inject(dealerStore);
  readonly featureToggleStore = inject(featureToggleStore);
  readonly dateAdapter = inject(DateAdapter);
  readonly dateFormats = inject<MatDateFormats>(MAT_DATE_FORMATS);
  readonly partsShopDealerStore = inject(partsShopDealerStore);

  public readonly initSubtitle = this.initializeSubtitle();
  public readonly rehydratePending$$ = this.initializeRehydratePending();

  inlineToastSubject = new BehaviorSubject<{
    status: boolean;
    timeToLeave: number;
  } | null>({
    status: true,
    timeToLeave: 0,
  });

  public isExpanded = false;
  public orgRegistered = signal<boolean>(false);
  public orgIsRejected = signal<boolean>(false);
  public pendingOrgSwitch$ = this.store.select(getPendingOrgSwitch);
  public pendingOrgRegistration$ = this.store.select(getRegisterOrganisationPending);
  public getOrganisationChanging$ = this.store.select(getOrganisationChanging);
  public validatedOrg = signal<boolean>(true);
  public internalOrg = signal<boolean>(false);
  public orgChanging = false;
  public orgId: string;
  public sideNavItems$$ = this.initializeNavigationItems();
  public stage = environment.stage;
  public features = Features;
  public currentUser = toSignal(this.store.select(userSelector));
  public currentOrganisation = toSignal(this.store.select(getCurrentOrganisation));

  public applications: ApplicationSwitcherApp[] = [
    {
      iconId: '750144',
      title: 'Machine Management',
      url: '',
      selected: true,
      dataTestId: 'machine-management',
      isInternal: true,
    },
    {
      iconId: '750146',
      title: 'Farm Management',
      url: environment.farmConnectBaseUrl + 'login/dashboard',
      selected: false,
      dataTestId: 'farm-management',
    },
  ];

  private smQuery: MediaQueryList;
  private currentOrganisation$$ = toSignal(this.store.select(getCurrentOrganisation));
  private orgChange$ = this.store.select(getLastSelectedOrganisation);
  private userPermissions$$ = toSignal(this.store.select(getUserPermissions));
  private registrationDefaultOrganisation$$ = toSignal(this.store.select(getRegistrationDefaultOrganisation));
  private dashboardInitialized$$ = toSignal(this.store.select(getDashboardIsInitialized));
  private permissions: string[] = [];
  private subscription = new Subscription();
  private orgChangeWatcher: Subscription;
  private userSubjectWatcher: Subscription;
  private initListener: () => void;
  private getRehydratePending$$ = toSignal(this.store.select(getRehydratePending));
  private lastAppliedMomentLocale = moment.locale();

  constructor() {
    const media = inject(MediaMatcher);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.smQuery = media.matchMedia(`(max-width: ${(screens as any).sm.max})`);

    this.initializePartsShop();
    this.initializeCurrentOrganizationHandler();
  }

  public handleLogoClick = () => this.gotoDashboard();

  public onApplicationSwitcherClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const machineManagementApp = this.applications.find(app => app.selected && app.isInternal);
    if (machineManagementApp && target.closest(`button[aria-labelledby="${machineManagementApp.title}"]`)) {
      this.eventTrackingService.eventTrackNew({
        payload: {
          event: 'machinemgtevent',
          eventCategory: 'Machine Management Header',
          eventAction: 'Click Button',
          eventName: 'Change to Machine Management',
        },
      });
    }
  }

  public ngAfterViewInit(): void {
    this.store.dispatch(new CheckLogin());
  }

  public ngOnInit(): void {
    this.userSubjectWatcher = this.auth.userProfileSubject$.subscribe((user: IUser) => {
      const queryParams = new URLSearchParams(window.location.search);
      const urlLang = queryParams.get('language')?.toLowerCase();
      let lang = urlLang ?? user.user_metadata.language?.toLowerCase();
      lang = lang ? lang : 'de';
      this.translator.use(lang);
      this.convertPipeService.setLanguage(lang);
      this.initializeUcUiLanguage(lang);
    });

    this.subscription.add(
      this.getOrganisationChanging$.subscribe((isChanging: boolean) => {
        this.orgChanging = isChanging;
      })
    );

    this.subscription.add(
      this.machineService.unit$.subscribe((zone: string) => {
        this.convertPipeService.setTimeZone(zone);
      })
    );

    // Keep the moment/Material date-adapter locale (used by all mat-datepicker calendars) in sync with the
    // user's chosen UI language, rather than the organisation's country.
    this.subscription.add(
      this.translator.onLangChange.subscribe(({ lang }) => {
        this.initMomentLocale(lang);
      })
    );
  }

  public ngOnDestroy(): void {
    this.maintenanceTextService.clearMaintenanceTexts();
    this.subscription.unsubscribe();
    this.orgChangeWatcher?.unsubscribe();
    this.userSubjectWatcher?.unsubscribe();

    window.removeEventListener('UC_UI_INITIALIZED', this.initListener);
  }

  public getActionFromNavbar(event: boolean): void {
    this.isExpanded = event;
    this.dashboardService.checkNavbarStatus(this.isExpanded);
  }

  public handleClickedEvent(_event: boolean) {
    if (this.smQuery.matches && this.isExpanded) {
      this.isExpanded = !this.isExpanded;
      this.dashboardService.checkNavbarStatus(this.isExpanded);
    }
  }

  public getCurrentOrganisation() {
    // if current org is reset to null the subscription might be built up again
    // and thus the old one needs to be unsubscribed first
    this.orgChangeWatcher?.unsubscribe();
    this.orgChangeWatcher = this.orgChange$.subscribe((data: Partial<IOrganisationListItem | null>) => {
      if (data && data.auth0Id) {
        this.store.dispatch(new GetCurrentOrganisation(data.auth0Id));
      }
    });
  }

  private initializeUcUiLanguage(language: string) {
    if (!language) {
      return;
    }

    language = language?.toLocaleLowerCase();
    // usercentrics only knows en
    if (language === 'en-us' || language === 'en-gb') {
      language = 'en';
    }

    if (typeof UC_UI !== 'undefined' && UC_UI?.isInitialized()) {
      UC_UI.updateLanguage(language);
    } else {
      this.initListener = () => UC_UI.updateLanguage(language);
      window.addEventListener('UC_UI_INITIALIZED', this.initListener);
    }
  }

  private initializeCurrentOrganizationHandler() {
    effect(() => {
      const currentOrg = this.currentOrganisation$$();

      const featureToggles = this.featureToggleStore.featureToggles();
      if ((featureToggles?.length ?? 0) <= 0) {
        return;
      }

      this.initializeCurrentOrganization(currentOrg ?? null);

      // call initializeCurrentOrganization first to check if current org needs to be requested
      if (currentOrg == null) {
        return;
      }

      this.cdr.detectChanges();
    });
  }

  private initializeCurrentOrganization(currentOrg: ICurrentOrganisation | null) {
    if (currentOrg) {
      this.validatedOrg.set(currentOrg.validated);
      this.convertPipeService.setUnitSystem(currentOrg.unitSystem);
      this.convertPipeService.setCountry(currentOrg.country);
      this.updateDateFormatForCountry(currentOrg.country);
      this.orgRegistered.set(currentOrg.registered);
      this.orgIsRejected.set(currentOrg.registrationStatus === RegistrationStatus.REJECTED);
      this.orgId = currentOrg.auth0Id;
      this.internalOrg.set(currentOrg.internal ?? false);
      this.store.dispatch(new MachineImagesUrl());
      this.updateFarmManagementApplicationLink(currentOrg.auth0Id);
    } else {
      this.getCurrentOrganisation();
    }

    this.cdr.detectChanges();
  }

  private initializeNavigationItems() {
    return computed(() => {
      const currentOrganisation = this.currentOrganisation$$();
      if (currentOrganisation == null) {
        return null;
      }

      const userPermissions = this.userPermissions$$();
      const featureTogglesInitialized = this.featureToggleStore.featureTogglesInitialized();
      const hasValidData = (userPermissions?.length ?? 0) > 0 && featureTogglesInitialized;
      if (!hasValidData) {
        return null;
      }

      this.permissions = userPermissions as string[];

      // when on the dashboard page wait until it is completely initialized before filling the navigation items
      // and with that enable the navigation
      const isDashboardPage = window.location.pathname.startsWith('/dashboard');
      const dashboardInitialized = !isDashboardPage || this.dashboardInitialized$$();
      if (!dashboardInitialized) {
        return null;
      }

      if (this.rehydratePending$$() || this.registrationDefaultOrganisation$$() == null) {
        return null;
      }

      return this.updateNavigationItems();
    });
  }

  private updateNavigationItems() {
    return navigationItems.map((item: NavigationItem) => this.processNavigationItem(item)) as NavigationItem[];
  }

  private processNavigationItem(item: IFeatureToggleNavigationItem | NavigationItem) {
    const alternativeRoute = (item as IFeatureToggleNavigationItem).featureToggleDescription?.alternativeRoute;
    const featureIsUntoggled = this.isFeatureUntoggled(item);

    return featureIsUntoggled && alternativeRoute
      ? this.buildNavigationItem(alternativeRoute)
      : this.buildNavigationItem(item, featureIsUntoggled);
  }

  private buildNavigationItem(item: NavigationItem, featureIsUntoggled?: boolean) {
    const isDisabled = this.isDisabled(item);
    const processedItem: IFeatureToggleNavigationItem = {
      ...item,
      label: this.translator.instant(item.label),
      disable: isDisabled,
      children: item.children?.map(child => this.processNavigationItem(child)),
    };

    processedItem.hidden = item.hidden || featureIsUntoggled || isDisabled;

    return processedItem;
  }

  private isFeatureUntoggled(e: IFeatureToggleNavigationItem): boolean {
    if (!e.route) {
      return false;
    }

    if (!this.orgRegistered() && ['license', 'file-transfer'].includes(e.route)) {
      return true;
    }

    const featureToggleDescription = e.featureToggleDescription;
    if (featureToggleDescription == null) {
      return false;
    }

    const toggleIsActive = this.calculateIfFeatureToggleActive(featureToggleDescription, e.children ?? []);

    return !toggleIsActive;
  }

  private calculateIfFeatureToggleActive(
    featureToggleDescription: FeatureToggleDescription,
    children: IFeatureToggleNavigationItem[]
  ) {
    const features = getFeatures(featureToggleDescription, children);
    if (!Array.isArray(features)) {
      return this.isFeatureToggleActive(features as string);
    }

    if (featureToggleDescription.operator === 'OR') {
      return features.some(ft => this.isFeatureToggleActive(ft));
    }

    return features.every(ft => this.isFeatureToggleActive(ft));
  }

  private isDisabled(e: NavigationItem): boolean {
    if (!e.route) {
      return false;
    }

    const permissions = permissionsPerRoute.get(e.route);
    return permissions != null && !this.permissions?.some(item => permissions.includes(item));
  }

  private gotoDashboard() {
    void this.router.navigate(['/']);
  }

  private updateFarmManagementApplicationLink(orgId: string) {
    this.applications.forEach(app => {
      if (app.title !== 'Farm Management') {
        return;
      }

      app.url = replaceOrUpdateQueryParam('orgId', orgId, app.url);
    });
  }

  private initializePartsShop() {
    effect(() => {
      // To be able to load the parts shop cart
      const currentOrganisation = this.currentOrganisation$$();
      if (currentOrganisation == null) {
        return;
      }

      const featureToggles = this.featureToggleStore.featureToggles();
      if (!featureToggles?.some(toggle => toggle.featureId === Features.NEW_PARTS_SHOP)) {
        return;
      }

      if (currentOrganisation?.dealer?.id) {
        this.partsShopDealerStore.getDealer({ dealerId: currentOrganisation.dealer.id });
      }
    });
  }

  private isFeatureToggleActive(featureToggle: string) {
    const featureToggles = this.featureToggleStore.featureToggles();
    return featureToggles.some(item => featureToggle === item.featureId);
  }

  private initMomentLocale(lang: string) {
    const normalizedLang = lang.toLowerCase();
    const requestedLocale = LANGUAGE_TO_MOMENT_LOCALE[normalizedLang] ?? normalizedLang;
    const resolvedLocale = moment.locale(requestedLocale); // e.g. 'ar-eg' resolves to 'ar'

    if (resolvedLocale === this.lastAppliedMomentLocale) {
      return;
    }
    this.lastAppliedMomentLocale = resolvedLocale;

    // Arabic locales render dates with Arabic-Indic digits via moment's "postformat" hook by default,
    // which breaks JSON parsing in some APIs - force plain digits for them.
    if (resolvedLocale.startsWith('ar')) {
      moment.updateLocale(resolvedLocale, { postformat: (str: string) => str });
    }

    this.dateAdapter.setLocale(resolvedLocale);
    this.cdr.markForCheck();
  }

  // The datepicker's numeric format (day/month/year order and separators) must reflect the
  // organisation's country, independent of the UI language used for month/weekday names above -
  // 'L'/'LT' tokens are resolved against whichever locale renders the calendar, so we pin them
  // here to a literal, locale-independent pattern (e.g. 'DD.MM.YYYY') derived from the country.
  private updateDateFormatForCountry(country: string) {
    const countryLocale = getLocaleByAlpha2(country.toUpperCase());
    if (!countryLocale) {
      return;
    }

    const localeData = moment.localeData(countryLocale);
    const dateFormat = localeData.longDateFormat('L');
    const timeFormat = localeData.longDateFormat('LT');

    this.dateFormats.parse.dateInput = dateFormat;
    this.dateFormats.display.dateInput = dateFormat;
    this.dateFormats.parse.timeInput = timeFormat;
    this.dateFormats.display.timeInput = timeFormat;
    this.dateFormats.display.timeOptionLabel = timeFormat;
  }

  private initializeSubtitle() {
    return computed(() => {
      if (this.routingState.isMachineSharingInvitationPage()) {
        return 'machine_sharing.invitation.header-title';
      }
      return undefined;
    });
  }

  private initializeRehydratePending() {
    return computed(() => {
      const featureToggleLoadingPending = this.featureToggleStore.listPending();
      return this.getRehydratePending$$() || featureToggleLoadingPending;
    });
  }
}
